'use client';

import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import type { OperationsDashboardResult } from '@/lib/operations-dashboard';
import OperationsDashboardView from './components/OperationsDashboardView';

export default function OperationsDashboardPage() {
  const [data, setData] = useState<OperationsDashboardResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/runtime/operations-dashboard', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = (await response.json()) as OperationsDashboardResult;
      setData(payload);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Unavailable');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  return (
    <AppLayout data-component-id="src/app/operations-dashboard/page.tsx:36:4" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="36" data-component-end-line="76" data-component-file="page.tsx" data-component-name="AppLayout" data-component-content="%7B%22elementName%22%3A%22AppLayout%22%7D"
      title="Operations Dashboard"
      subtitle="RTWO-OPDB-001 · Node 01 · Truthful operational information only"
    >
      <div data-component-id="src/app/operations-dashboard/page.tsx:40:6" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="40" data-component-end-line="75" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
        <div data-component-id="src/app/operations-dashboard/page.tsx:41:8" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="41" data-component-end-line="53" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20gap-3%22%7D" className="flex items-center justify-between gap-3">
          <p data-component-id="src/app/operations-dashboard/page.tsx:42:10" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="42" data-component-end-line="45" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Architecture%20v1.0%20frozen%20%C2%B7%20Public%20Testnet%20not%20authorized%20%C2%B7%20Port%204028%20not%20publicly%5Cn%20%20%20%20%20%20%20%20%20%20%20%20exposed%22%7D" className="text-sm text-muted-foreground">
            Architecture v1.0 frozen · Public Testnet not authorized · Port 4028 not publicly
            exposed
          </p>
          <button data-component-id="src/app/operations-dashboard/page.tsx:46:10" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="46" data-component-end-line="52" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22rounded-md%20border%20border-border%20px-3%20py-1.5%20text-xs%20font-medium%20hover%3Abg-muted%2F40%22%2C%22textContent%22%3A%22Refresh%22%7D"
            type="button"
            onClick={() => void loadDashboard()}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div data-component-id="src/app/operations-dashboard/page.tsx:56:10" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="56" data-component-end-line="58" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-border%20bg-card%20p-6%20text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20operations%20dashboard%E2%80%A6%22%7D" className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading operations dashboard…
          </div>
        ) : null}

        {error ? (
          <div data-component-id="src/app/operations-dashboard/page.tsx:62:10" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="62" data-component-end-line="64" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-negative%2F30%20bg-negative%2F5%20p-6%20text-sm%20text-negative%22%2C%22textContent%22%3A%22Operations%20dashboard%20unavailable%3A%22%7D" className="rounded-xl border border-negative/30 bg-negative/5 p-6 text-sm text-negative">
            Operations dashboard unavailable: {error}
          </div>
        ) : null}

        {data ? (
          <OperationsDashboardView data-component-id="src/app/operations-dashboard/page.tsx:68:10" data-component-path="src/app/operations-dashboard/page.tsx" data-component-line="68" data-component-end-line="73" data-component-file="page.tsx" data-component-name="OperationsDashboardView" data-component-content="%7B%22elementName%22%3A%22OperationsDashboardView%22%7D"
            sections={data.sections}
            source={data.source}
            nodeId={data.nodeId}
            collectedAt={data.collectedAt}
          />
        ) : null}
      </div>
    </AppLayout>
  );
}
